import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    detectSessionInUrl: true,
    persistSession: true,
    autoRefreshToken: true,
    flowType: 'pkce',
  },
});

// Parses an OAuth provider redirect for either a successful callback
// (code / access_token present) or an error returned by the provider.
export const getOAuthCallbackInfo = () => {
  const hash = window.location.hash ? window.location.hash.replace(/^#/, '') : '';
  const search = window.location.search ? window.location.search.replace(/^\?/, '') : '';
  const params = new URLSearchParams(hash || search);

  const error =
    params.get('error_description') || params.get('error') || null;
  const hasAuth = params.has('code') || params.has('access_token');

  return { hasAuth, error };
};

// If the current URL is an OAuth provider *error* redirect, stash the message
// for the originating screen (Login or Sign Up) and return the path the user
// should be sent to. Returns null when there is no OAuth error to handle.
export const resolveOAuthErrorRedirect = () => {
  const { error } = getOAuthCallbackInfo();
  if (!error) {
    return null;
  }

  sessionStorage.setItem('oauth_error', error);
  const origin = sessionStorage.getItem('oauth_origin');
  sessionStorage.removeItem('oauth_origin');

  return origin === 'signup' ? '/signUp' : '/login';
};

export const signUpWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: window.location.origin + '/chatbot',
    },
  });

  if (error) {
    if (error.message.includes('already registered')) {
      throw new Error('Email already in use.');
    } else if (error.message.includes('password')) {
      throw new Error('Password should be at least 6 characters.');
    }
    throw error;
  }

  return data;
};

export const signInWithEmail = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin + '/chatbot',
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const signInWithFacebook = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'facebook',
    options: {
      redirectTo: window.location.origin + '/chatbot',
    },
  });

  if (error) {
    throw error;
  }

  return data;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
};

export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
};

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin + '/login',
  });

  if (error) {
    throw error;
  }

  return data;
};
